import {
  Box,
  Container,
  Divider
} from '@mui/material';
import ActionButton from "../../components/common/buttons/ActionButton";
import CancelButton from "../../components/common/buttons/CancelButton";
import { useCallback, useEffect, useMemo, useState, useTransition } from "react";

import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ERR_OUTLET_HAS_PENDING_CHANGE_REQUEST, getApiErrorCode, getApiErrorMessage } from "../../utils/apiError";
import { AccountSettingsSection } from "./settings/AccountSettingSection";
import { PaymentMethodSection } from "./settings/PaymentMethodSection";
import { RateOrderSection } from "./settings/RateOrderSection";
import { ShopStatusSection } from "./settings/SalonStatusSection";
import { useNavigate, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { SalonAPI } from '../../api/Salon';

interface SalonProps {
  settingData: SalonSetting | null;
  onChange: (data: SalonSetting) => void;
  onCreateSalon?: (data: SalonSetting) => void;
}

const defaultSettings: SalonSetting = {
  status: "",
  payment_method_limit: "",
  email: "",
  password: "",
  top_rate_order: 0,
};

const normalizeSalonSetting = (data?: Partial<SalonSetting> | null): SalonSetting => {
  const setting = data as Partial<SalonSetting> & { owner_email?: string } | null | undefined;

  return {
    ...defaultSettings,
    ...setting,
    email: setting?.email || setting?.owner_email || "",
    password: setting?.password || "",
    status: setting?.status || defaultSettings.status,
    payment_method_limit: setting?.payment_method_limit || defaultSettings.payment_method_limit,
    top_rate_order: setting?.top_rate_order ?? defaultSettings.top_rate_order,
  };
};

export function Settings({ settingData , onChange, onCreateSalon } : SalonProps) {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [, startTransition] = useTransition();
  const [settings, setSettings] = useState<SalonSetting>(defaultSettings);
  const [confirmPassword, setConfirmPassword] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const debouncedOnChange = useMemo(() => debounce(onChange, 300), [onChange]);

  useEffect(() => {
    if (settingData) {
      setSettings(normalizeSalonSetting(settingData));
    }
  }, [settingData]);

  useEffect(() => {
    if (!id && settings) {
      debouncedOnChange(settings);
    }
    // Cleanup debounce on unmount
    return () => debouncedOnChange.cancel();
  }, [settings, id, debouncedOnChange]);

  const handleSave = async () => {
    const payload = normalizeSalonSetting({
      ...settingData,
      ...settings,
      status: settings.status || settingData?.status || "WAIT_FOR_REVIEW",
      payment_method_limit: settings.payment_method_limit || settingData?.payment_method_limit || "BOTH",
      top_rate_order: settings.top_rate_order ?? settingData?.top_rate_order ?? 0,
    });

    // Validation
    if (!id && !payload.email) {
      toast.error(t("salon.settings.validation.email_required"));
      return;
    }

    if (!payload.password && confirmPassword) {
      toast.error(t("salon.settings.validation.password_required"));
      return;
    }

    if (payload.password && payload.password.length < 6) {
      toast.error(t("salon.settings.validation.password_min"));
      return;
    }

    if (payload.password && payload.password !== confirmPassword) {
      toast.error(t("salon.settings.validation.password_mismatch"));
      return;
    }

    setIsLoading(true);
    
    if (id) {
      SalonAPI.updateSalonSetting(id, payload).then(() => {
        toast.success(t("salon.settings.messages.saved"));
        navigate("/salon");
      }).catch((error) => {
        toast.error(
          getApiErrorCode(error) === ERR_OUTLET_HAS_PENDING_CHANGE_REQUEST
            ? t("salon.settings.messages.pending_change_request")
            : getApiErrorMessage(error, t("salon.settings.messages.save_failed"))
        );
      }).finally(() => {
        setIsLoading(false);
      });
    }
  };

  const handleCancel = () => {
    setSettings(normalizeSalonSetting(settingData));
    setConfirmPassword("");
    toast.info(t("salon.settings.messages.cancelled"));
  };

  const handleClickAction = () => {
    if (onCreateSalon && !id) {
      onCreateSalon(settings);
    } else {
      handleSave();
    }
  };

  // Memoized handlers to prevent re-renders
  const handleEmailChange = useCallback((value: string) => {
    setSettings(prev => ({ ...prev, email: value }));
  }, []);

  const handlePasswordChange = useCallback((value: string) => {
    setSettings(prev => ({ ...prev, password: value }));
  }, []);

  const handleStatusChange = useCallback((value: any) => {
    startTransition(() => {
      setSettings(prev => ({ ...prev, status: value }));
    });
  }, []);

  const handlePaymentChange = useCallback((value: string) => {
    startTransition(() => {
      setSettings(prev => ({ ...prev, payment_method_limit: value }));
    });
  }, []);

  const handleRateChange = useCallback((value: number) => {
    startTransition(() => {
      setSettings(prev => ({ ...prev, top_rate_order: value }));
    });
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Settings Form */}
      <Container maxWidth="md" sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <ShopStatusSection
            value={settings.status}
            onChange={handleStatusChange}
          />

          <PaymentMethodSection
            value={settings.payment_method_limit}
            onChange={handlePaymentChange}
          />

          <AccountSettingsSection
            email={settings.email}
            password={settings.password}
            confirmPassword={confirmPassword}
            onEmailChange={handleEmailChange}
            onPasswordChange={handlePasswordChange}
            onConfirmPasswordChange={setConfirmPassword}
            isEdit={!!id}
          />

          <RateOrderSection
            value={settings.top_rate_order}
            onChange={handleRateChange}
          />

          <Divider sx={{ my: 2 }} />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2 }}>
            <CancelButton onClick={handleCancel} disabled={isLoading}>
              {t("btn.cancel")}
            </CancelButton>
            <ActionButton
              onClick={handleClickAction}
              disabled={isLoading || (!!id && !settingData)}
            >
              {isLoading ? t("salon.settings.saving") : t("btn.save")}
            </ActionButton>
            
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
